import { useEffect, useRef, useState } from 'react';
import { useFeedbackDraft, MAX_FEEDBACK_DETAILS } from '../../store/feedback-draft-store';
import type { FeedbackType } from '../../services/feedback-api';
import { FeedbackInbox } from './FeedbackInbox';

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/**
 * HQ Feedback side panel. Every signed-in HQ person gets the Send tab (a Bug or an Idea); the HQ Owner, and only the HQ Owner, also
 * gets an Inbox tab with the reports HQ has received. `showInbox` is a UI hint from the same Cognito `owner` group the server checks:
 * the inbox list and every status change are enforced by the Feedback function itself, so an admin who is not the Owner gets no tab
 * and a 403 from a direct call.
 *
 * It is a non-blocking drawer (no backdrop), rendered as a sibling of the page - never around it - so opening it cannot remount or
 * change whatever the person is doing. Escape closes it; focus moves to the first field on open and back to the opener on close.
 *
 * The person's name, e-mail, role and the time are NOT collected here: the server records them from the verified sign-in and its
 * own clock.
 */

interface Props {
  onClose: () => void;
  showInbox: boolean;
}

function SendForm() {
  const { type, details, submitting, error, conflictReference, confirmation, setType, setDetails, submit, startAnother } = useFeedbackDraft();
  const textRef = useRef<HTMLTextAreaElement>(null);
  const over = details.length > MAX_FEEDBACK_DETAILS;
  const canSend = details.trim().length > 0 && !over && !submitting;

  useEffect(() => {
    if (!confirmation) textRef.current?.focus();
  }, [confirmation]);

  if (confirmation) {
    return (
      <div className="p-4 space-y-3" data-testid="feedback-confirmation">
        <p role="status" className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
          Thanks, your feedback was received.
          <span className="block text-xs text-emerald-700 mt-1">Reference <strong data-testid="feedback-reference">{confirmation.reference}</strong>{confirmation.replayed ? ' (already recorded)' : ''}</span>
        </p>
        <button type="button" onClick={startAnother} className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-800 hover:bg-gray-50">
          Send another
        </button>
      </div>
    );
  }

  return (
    <form
      className="p-4 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSend) void submit(window.location.pathname);
      }}
      noValidate
    >
      <fieldset>
        <legend className="text-xs font-medium text-gray-700 mb-1">What is it?</legend>
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden" role="radiogroup" aria-label="Feedback type">
          {(['Bug', 'Idea'] as FeedbackType[]).map((t) => (
            <label key={t} className={`px-4 py-1.5 text-xs font-medium cursor-pointer ${type === t ? 'bg-[#12223f] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
              <input type="radio" name="feedback-type" value={t} checked={type === t} onChange={() => setType(t)} className="sr-only" />
              {t}
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label htmlFor="feedback-details" className="text-xs font-medium text-gray-700">
          {type === 'Bug' ? 'What went wrong?' : 'What would make this better?'}
        </label>
        <textarea
          id="feedback-details"
          ref={textRef}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={7}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4AABDE]"
          aria-describedby="feedback-help feedback-count"
          aria-invalid={over}
          disabled={submitting}
        />
        <div className="flex items-center justify-between mt-1">
          <p id="feedback-help" className="text-[11px] text-gray-500">We include who you are, the time and the page you are on. Please do not paste passwords.</p>
          <p id="feedback-count" className={`text-[11px] whitespace-nowrap flex-shrink-0 ml-3 ${over ? 'text-red-700 font-semibold' : 'text-gray-400'}`}>{details.length} / {MAX_FEEDBACK_DETAILS}</p>
        </div>
      </div>

      {error && conflictReference && (
        <p role="alert" className="text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-md px-3 py-2" data-testid="feedback-conflict">
          An earlier attempt was already received as <strong>{conflictReference}</strong> with different text, so this edited text was <strong>not</strong> saved.
          Your text is still here. Send it as a new report if you want it recorded as well.
        </p>
      )}
      {error && !conflictReference && (
        <p role="alert" className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="feedback-error">
          Your feedback was not sent. {error} Your text is still here, so you can try again.
        </p>
      )}

      <button
        type="submit"
        disabled={!canSend}
        data-testid="feedback-send"
        className="px-4 py-1.5 text-xs font-semibold rounded-md bg-[#12223f] text-white hover:bg-[#1a2b3d] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Sending…' : conflictReference ? 'Send as new report' : error ? 'Try again' : 'Send feedback'}
      </button>
    </form>
  );
}

export function FeedbackPanel({ onClose, showInbox }: Props) {
  const clear = useFeedbackDraft((s) => s.startAnother);
  const [tab, setTab] = useState<'send' | 'inbox'>('send');

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A shown confirmation belongs to the visit that produced it; reopening starts a fresh form.
  useEffect(() => () => clear(), [clear]);

  return (
    <aside
      role="dialog"
      aria-label="Feedback"
      className="fixed top-0 right-0 z-[60] h-full w-full sm:w-[26rem] bg-white shadow-2xl border-l border-gray-200 flex flex-col"
      data-testid="feedback-panel"
    >
      <div className="flex items-center justify-between px-4 h-12 bg-[#12223f] border-b border-white/10 flex-shrink-0">
        <h2 className="text-sm font-semibold text-white">Feedback</h2>
        <button type="button" onClick={onClose} aria-label="Close feedback" className="p-1 rounded text-gray-300 hover:text-white hover:bg-white/10">
          <XIcon className="w-4 h-4" />
        </button>
      </div>

      {/* The Inbox tab exists only for the HQ Owner. Everyone else sees just the form. */}
      {showInbox && (
        <div role="tablist" aria-label="Feedback" className="flex border-b border-gray-200 flex-shrink-0">
          {([['send', 'Send'], ['inbox', 'Inbox']] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`feedback-tab-${id}`}
              aria-selected={tab === id}
              aria-controls={`feedback-tabpanel-${id}`}
              onClick={() => setTab(id)}
              className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px ${tab === id ? 'border-[#4AABDE] text-[#12223f]' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* The form stays mounted (hidden) while the Inbox is showing, so nothing typed is ever lost by switching tabs. */}
      <div className="flex-1 min-h-0 overflow-y-auto" id="feedback-tabpanel-send" role={showInbox ? 'tabpanel' : undefined} aria-labelledby={showInbox ? 'feedback-tab-send' : undefined} hidden={tab !== 'send'}>
        <SendForm />
      </div>
      {showInbox && tab === 'inbox' && (
        <div className="flex-1 min-h-0" id="feedback-tabpanel-inbox" role="tabpanel" aria-labelledby="feedback-tab-inbox">
          <FeedbackInbox />
        </div>
      )}
    </aside>
  );
}
